import { useState } from 'react';
import { MapPin, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { SchoolMatch } from '../../types/import';

interface SchoolMatchReviewProps {
  match: SchoolMatch;
  onReview: (matchId: string, status: string, orgId?: string) => void;
}

function scoreColor(score: number): string {
  if (score >= 90) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (score >= 80) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
}

/**
 * Two-column card for reviewing a single school match
 */
export const SchoolMatchReview = ({ match, onReview }: SchoolMatchReviewProps) => {
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    match.matchedOrgId || null
  );
  // AUTO_MATCHED rows start collapsed (already runnable); this flips them back
  // to the full candidate grid so the reviewer can pick something else.
  const [isChangingMatch, setIsChangingMatch] = useState(false);

  const candidates = match.candidateOrgIds || [];
  const isReviewed = match.status === 'CONFIRMED' || match.status === 'CREATE_NEW' || match.status === 'SKIPPED';
  const isAutoMatched = match.status === 'AUTO_MATCHED';
  const isCollapsedAutoMatch = isAutoMatched && !isChangingMatch;
  const showActions = !isReviewed && !isCollapsedAutoMatch;

  const cancelChangeMatch = () => {
    setSelectedOrgId(match.matchedOrgId || null);
    setIsChangingMatch(false);
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${isReviewed ? 'opacity-60' : ''}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: Source school data */}
        <div>
          <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
            Source
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {match.sourceSchoolName}
          </h3>
          <div className="mt-1 space-y-0.5 text-sm text-gray-600 dark:text-gray-400">
            {match.sourceAddress && (
              <div className="flex items-center space-x-1">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span>{match.sourceAddress}</span>
              </div>
            )}
            <div>
              {[match.sourceCity, match.sourceState, match.sourceZip].filter(Boolean).join(', ')}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              Code: {match.sourceCode}
            </div>
          </div>
        </div>

        {/* Right: Candidates */}
        <div>
          <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
            Candidates
          </div>
          {isCollapsedAutoMatch ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
              <div className="flex items-center space-x-2 text-sm text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <span>
                  Auto-matched to{' '}
                  <span className="font-semibold">{match.matchedOrg?.name ?? 'an existing school'}</span>
                  {' '}— ready to run, no action needed.
                </span>
              </div>
              <button
                onClick={() => setIsChangingMatch(true)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors whitespace-nowrap"
              >
                Change match
              </button>
            </div>
          ) : candidates.length > 0 ? (
            <div className="space-y-2">
              {candidates.map((candidate) => (
                <label
                  key={candidate.orgId}
                  className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${
                    selectedOrgId === candidate.orgId
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name={`match-${match.id}`}
                      checked={selectedOrgId === candidate.orgId}
                      onChange={() => setSelectedOrgId(candidate.orgId)}
                      disabled={isReviewed}
                      className="text-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {candidate.orgName}
                      </div>
                      {candidate.orgAddress && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {candidate.orgAddress}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {[candidate.orgCity, candidate.orgState, candidate.orgZip].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${scoreColor(candidate.score)}`}>
                    {candidate.score}%
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 py-3">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span>No matches found</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex items-center space-x-3 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => {
              if (selectedOrgId) {
                onReview(match.id, 'CONFIRMED', selectedOrgId);
              }
            }}
            disabled={!selectedOrgId}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm Match
          </button>
          <button
            onClick={() => onReview(match.id, 'CREATE_NEW')}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Create New School
          </button>
          <button
            onClick={() => onReview(match.id, 'SKIPPED')}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 transition-colors"
          >
            Skip
          </button>
          {isChangingMatch && (
            <button
              onClick={cancelChangeMatch}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Reviewed badge */}
      {isReviewed && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
            match.status === 'CONFIRMED'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : match.status === 'CREATE_NEW'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
          }`}>
            {match.status === 'CONFIRMED' && 'Confirmed'}
            {match.status === 'CREATE_NEW' && 'Create New'}
            {match.status === 'SKIPPED' && 'Skipped'}
          </span>
          {match.status === 'CONFIRMED' && match.matchedOrg && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              Matched to: {match.matchedOrg.name}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
